import React from "react";
import { 
  FormControl, 
  FormLabel, 
  Select, 
  MenuItem, 
  TextField,
  Typography,
  Chip,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useNode } from "@craftjs/core";

export const Container = ({
  name,
  hidden,
  style,
  // Bulma additions
  bulmaClass = '',
  modifiers = [],
  children,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  if (hidden) return null;

  // Build Bulma className
  const bulmaClassName = [bulmaClass, ...modifiers].filter(Boolean).join(' ');

  // Selection styling
  const getSelectionStyle = () => {
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px',
        backgroundColor: 'rgba(33, 150, 243, 0.05)'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px',
        backgroundColor: 'rgba(255, 152, 0, 0.03)'
      };
    }
    return {};
  };

  return (
    <div
      ref={ref => connect(drag(ref))}
      name={name}
      className={bulmaClassName}
      style={{
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export const ContainerSettings = () => {
  const {
    name,
    bulmaClass,
    modifiers,
    actions: { setProp }
  } = useNode(node => ({
    name: node.data.props.name,
    bulmaClass: node.data.props.bulmaClass,
    modifiers: node.data.props.modifiers
  }));

  // Bulma Container Classes
  const bulmaContainerClasses = [
    'container', 'section', 'hero', 'hero-body', 'hero-head', 'hero-foot',
    'level', 'level-left', 'level-right', 'level-item',
    'media', 'media-left', 'media-content', 'media-right',
    'columns', 'column',
    'box', 'card', 'card-header', 'card-content', 'card-footer',
    'notification', 'message', 'message-header', 'message-body',
    'panel', 'panel-heading', 'panel-block',
    'tile', 'tile-parent', 'tile-child'
  ];

  // Bulma Modifiers for containers
  const bulmaModifiers = {
    Colors: [
      'is-primary', 'is-link', 'is-info', 'is-success', 'is-warning', 'is-danger',
      'is-black', 'is-dark', 'is-light', 'is-white'
    ],
    Sizes: [
      'is-small', 'is-medium', 'is-large',
      'is-fullheight', 'is-fullwidth'
    ],
    Layout: [
      'is-flex', 'is-block', 'is-inline', 'is-inline-block', 'is-inline-flex',
      'has-text-centered', 'has-text-left', 'has-text-right',
      'is-justify-content-center', 'is-justify-content-space-between',
      'is-align-items-center', 'is-align-items-flex-start', 'is-align-items-flex-end',
      'is-flex-direction-column', 'is-flex-direction-row'
    ],
    Responsive: [
      'is-mobile', 'is-tablet', 'is-desktop', 'is-widescreen', 'is-fullhd',
      'is-hidden-mobile', 'is-hidden-tablet', 'is-hidden-desktop'
    ],
    States: [
      'is-active', 'is-loading', 'is-outlined', 'is-transparent'
    ]
  };

  const toggleModifier = (modifier) => {
    const currentModifiers = modifiers || [];
    const newModifiers = currentModifiers.includes(modifier)
      ? currentModifiers.filter(m => m !== modifier)
      : [...currentModifiers, modifier];
    setProp(props => props.modifiers = newModifiers);
  };

  const isModifierActive = (modifier) => {
    return (modifiers || []).includes(modifier);
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      {/* Bulma Classes Section */}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Bulma Container Classes
      </Typography>
      
      <FormControl fullWidth margin="normal">
        <FormLabel>Base Class</FormLabel>
        <Select
          value={bulmaClass || ''}
          onChange={(e) => setProp(props => props.bulmaClass = e.target.value)}
          size="small"
        >
          <MenuItem value="">None</MenuItem>
          {bulmaContainerClasses.map(cls => (
            <MenuItem key={cls} value={cls}>{cls}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Bulma Modifiers
      </Typography>

      {Object.entries(bulmaModifiers).map(([category, mods]) => (
        <Accordion key={category}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body2">{category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {mods.map(modifier => (
                <Grid item key={modifier}>
                  <Chip
                    label={modifier}
                    variant={isModifierActive(modifier) ? "filled" : "outlined"}
                    color={isModifierActive(modifier) ? "secondary" : "default"}
                    size="small"
                    clickable
                    onClick={() => toggleModifier(modifier)}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Active Classes
      </Typography>
      <Box sx={{ mb: 2 }}>
        {bulmaClass && (
          <Chip 
            label={bulmaClass} 
            color="primary" 
            size="small" 
            sx={{ mr: 1, mb: 1 }}
            onDelete={() => setProp(props => props.bulmaClass = '')}
          />
        )}
        {(modifiers || []).map(modifier => (
          <Chip 
            key={modifier}
            label={modifier} 
            color="secondary" 
            size="small" 
            sx={{ mr: 1, mb: 1 }}
            onDelete={() => toggleModifier(modifier)}
          />
        ))}
      </Box>
    </div>
  );
};

export const ContainerDefaultProps = {
  name: "Container",
  bulmaClass: '',
  modifiers: []
};

Container.craft = {
  props: ContainerDefaultProps,
  related: {
    settings: ContainerSettings
  },
  displayName: 'Container'
};